import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { atomWithLoadable, mutationAtomWithLoadable } from 'lib/atoms';
import request from 'lib/request';
import type {
  FoucsDiagnosisResponse,
  CheckIntersectionsResponse,
  CoverageAnalysisResponse,
  CoverageEntry,
  DiagnosisState,
  RecalculateState,
} from './types';

export const foucsDiagnosisPageAtom = atom(1);
export const foucsDiagnosisPageSizeAtom = atom(100);
export const foucsDiagnosisSortAtom = atom<{
  column: string;
  direction: 'asc' | 'desc';
} | null>(null);
export const foucsDiagnosisAnalysisIdAtom = atom<string | null>(null);

const fetchPage = async (
  page: number,
  pageSize: number,
  sort: { column: string; direction: string } | null,
  analysisId: string | null
) => {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });
  if (sort) {
    params.set('sort_column', sort.column);
    params.set('sort_direction', sort.direction);
  }
  if (analysisId) {
    params.set('analysis_id', analysisId);
  }
  const response = await request.get<FoucsDiagnosisResponse>(
    `/admin/foucs_diagnosis?${params.toString()}`
  );
  if ('error' in response) {
    throw new Error(response.error);
  }
  return response;
};

export const [foucsDiagnosisAtom, foucsDiagnosisLoadableAtom] =
  atomWithLoadable((get) => {
    const page = get(foucsDiagnosisPageAtom);
    const pageSize = get(foucsDiagnosisPageSizeAtom);
    const sort = get(foucsDiagnosisSortAtom);
    const analysisId = get(foucsDiagnosisAnalysisIdAtom);
    return fetchPage(page, pageSize, sort, analysisId);
  });

export const checkIntersectionsAtomFamily = atomFamily(
  (scarId: number) =>
    atom<DiagnosisState, [DiagnosisState | 'fetch'], void>(
      { status: 'idle' },
      (get, set, action) => {
        if (action === 'fetch') {
          set(checkIntersectionsAtomFamily(scarId), { status: 'loading' });
          request
            .get<CheckIntersectionsResponse>(
              `/admin/foucs_diagnosis/${scarId}/check_intersections`
            )
            .then((response) => {
              if ('error' in response && typeof response.error === 'string') {
                set(checkIntersectionsAtomFamily(scarId), {
                  status: 'error',
                  error: response.error,
                });
              } else {
                set(checkIntersectionsAtomFamily(scarId), {
                  status: 'loaded',
                  data: response as CheckIntersectionsResponse,
                });
              }
            })
            .catch((err: Error) => {
              set(checkIntersectionsAtomFamily(scarId), {
                status: 'error',
                error: err.message,
              });
            });
        } else {
          set(checkIntersectionsAtomFamily(scarId), action);
        }
      }
    ),
  (a, b) => a === b
);

export const coverageResultsAtom = atom<Record<number, CoverageEntry | null>>(
  {}
);

export const [runCoverageAnalysisAtom, runCoverageAnalysisLoadableAtom] =
  mutationAtomWithLoadable<CoverageAnalysisResponse>(async () => {
    const abortController = new AbortController();
    // sync=1 runs the job in-process (no Sidekiq needed). Safe for dev/production —
    // the job is fast (~30s) and the polling fallback handles progress either way.
    const syncParam = '?sync=1';
    const response = await request.post<
      CoverageAnalysisResponse,
      { error: string }
    >(
      `/admin/foucs_diagnosis/run_coverage_analysis${syncParam}`,
      {},
      abortController
    );

    if (response.type === 'error') {
      throw new Error(
        response.data?.error ?? 'Failed to start coverage analysis'
      );
    }

    if (response.type === 'fatal' || response.type === 'abort') {
      throw new Error(response.message);
    }

    return (response as { type: 'success'; data: CoverageAnalysisResponse })
      .data;
  });

export const recalculateAtomFamily = atomFamily(
  (scarId: number) =>
    atom<RecalculateState, [RecalculateState | 'recalculate'], void>(
      { status: 'idle' },
      (get, set, action) => {
        if (action === 'recalculate') {
          set(recalculateAtomFamily(scarId), { status: 'loading' });
          request
            .post(
              `/admin/foucs_diagnosis/${scarId}/recalculate?sync=1`,
              {},
              new AbortController()
            )
            .then(() => {
              set(recalculateAtomFamily(scarId), { status: 'enqueued' });
            })
            .catch((err: Error) => {
              set(recalculateAtomFamily(scarId), {
                status: 'error',
                error: err.message,
              });
            });
        } else {
          set(recalculateAtomFamily(scarId), action);
        }
      }
    ),
  (a, b) => a === b
);
